import { useState } from 'react'
import {
  Drawer, Stack, Group, Text, Button, Textarea, Tooltip, Alert, Divider, Badge, Skeleton as MSkeleton,
} from '@mantine/core'
import type { ReactNode } from 'react'
import { IconCheck, IconRotate, IconAlertCircle, IconLock } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { EmptyState, MetaList, SectionTitle, SeverityBadge } from '@lighthouse/design'
import { RestrictedState } from '../access/RestrictedState'
import { FillRateHistory } from './FillRateHistory'
import {
  ACK_UNAVAILABLE_TEXT, SEVERITY_LABEL, SEVERITY_TONE, ackErrorText, acknowledgementScope, ageLabel, findingLinkView,
  provenanceLabel,
} from './combFormat'
import type { AcknowledgeGate, FindingLinkView } from './combFormat'
import { useAcknowledgeFinding, useUnacknowledgeFinding } from '../../hooks/useDataComb'
import { errorSummary, relativeTime } from '../../utils/format'
import { RULE_DESCRIPTIONS, RULE_LABELS } from '../../types/dataComb'
import type { CombFinding } from '../../types/dataComb'

/**
 * One finding, in full, with the measurement history behind it.
 *
 * A drawer rather than a modal because the reader is working through a list: the
 * table stays visible on the left, so closing one finding and opening the next
 * does not lose their place in it.
 *
 * ACKNOWLEDGING is gated on `gate` — the page's `acknowledgeGate(catalog,
 * isEntraConfigured())`, the catalogue's `canAcknowledgeCombFindings` — because
 * both writes are pinned to an Entra sign-in holding Lighthouse.Admin (R3). The
 * summary's `permission` is no longer read here.
 */
export function FindingDetailDrawer({
  finding,
  gate,
  entraConfigured = true,
  opened,
  onClose,
  requestedId = null,
  linkError = null,
  latestRunId = null,
}: {
  finding: CombFinding | null
  /** Whether acknowledging and restoring are offered (combFormat.ts `acknowledgeGate`). */
  gate: AcknowledgeGate
  /** Whether this build can sign anyone in with Entra — passed in, because src/auth reads window at load. */
  entraConfigured?: boolean
  opened: boolean
  onClose: () => void
  /**
   * The id a `?finding=` deep link asked for, while `finding` is still null:
   * the drawer then shows that request's state (loading, not found, failed)
   * instead of an empty panel.
   */
  requestedId?: string | null
  /** The deep link's error, if its request failed. */
  linkError?: unknown
  /** The latest completed pass, so a finding from an earlier one says what acknowledging it does. */
  latestRunId?: string | null
}) {
  const link = findingLinkView(finding, linkError)

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      title={
        finding ? (
          <Group gap="xs">
            <Text ff="monospace" fw={600}>{finding.target}</Text>
            <SeverityBadge
              severity={SEVERITY_TONE[finding.severity]}
              label={SEVERITY_LABEL[finding.severity]}
            />
            {finding.isNew && <Badge size="sm" variant="light">New</Badge>}
          </Group>
        ) : (
          requestedId && <Text ff="monospace" fw={600}>Finding {requestedId}</Text>
        )
      }
    >
      {/*
        Keyed on the finding, so moving to the next one REMOUNTS the body and its
        draft note goes with it. A reason typed against one finding must never
        still be in the box for the next — that is a wrong note attached to a
        permanent record, and resetting it in an effect would be a cascading
        render for the same result.
      */}
      {finding ? (
        <FindingDetail
          key={finding.id}
          finding={finding}
          gate={gate}
          entraConfigured={entraConfigured}
          onClose={onClose}
          latestRunId={latestRunId}
        />
      ) : (
        requestedId && <FindingLinkState view={link} requestedId={requestedId} error={linkError} />
      )}
    </Drawer>
  )
}

/**
 * A deep-linked finding that has not arrived: loading, missing, restricted or
 * failed, each visibly different. A missing finding is an answer (retention, or a
 * wrong link); a failed request is not one.
 *
 * Exported, like FindingDetail, only so combFormat.test.ts can render it: the
 * Drawer's portal renders nothing under react-dom/server.
 */
export function FindingLinkState({
  view,
  requestedId,
  error,
}: {
  view: FindingLinkView
  requestedId: string
  error: unknown
}) {
  if (view === 'not-found') {
    return (
      <EmptyState
        title={`No finding ${requestedId}`}
        message={
          'The server has no finding with that id. Findings are trimmed after the comb\'s retention window, '
          + 'so an old link can outlive what it pointed at; otherwise the link is wrong.'
        }
      />
    )
  }

  if (view === 'forbidden') {
    return <RestrictedState surface="This finding" reason={errorSummary(error)} />
  }

  if (view === 'error') {
    return (
      <EmptyState
        icon={<IconAlertCircle size={28} stroke={1.5} />}
        title={`Could not load finding ${requestedId}`}
        message={
          <>
            The request failed, which says nothing about the finding itself.
            {errorSummary(error) && <> The server said: {errorSummary(error)}</>}
          </>
        }
      />
    )
  }

  return (
    <Stack gap="md">
      <MSkeleton height={60} />
      <MSkeleton height={180} />
    </Stack>
  )
}

/**
 * The finding in full. Exported only for combFormat.test.ts (see FindingLinkState).
 *
 * By gate: `offer` — the note box and the buttons work. `refused` — the buttons are shown dimmed (`data-disabled`,
 * not `disabled`, so the tooltip with the server's reason still gets the pointer; the click is refused in the
 * handler), the note box is disabled, and the reason is said inline. `unavailable` — no button at all, and
 * ACK_UNAVAILABLE_TEXT. Who acknowledged it, and why, is a read and always shows.
 */
export function FindingDetail({
  finding,
  gate,
  entraConfigured = true,
  onClose,
  latestRunId,
}: {
  finding: CombFinding
  gate: AcknowledgeGate
  entraConfigured?: boolean
  onClose: () => void
  latestRunId: string | null
}) {
  const [note, setNote] = useState('')
  const acknowledge = useAcknowledgeFinding()
  const restore = useUnacknowledgeFinding()

  const isAcknowledged = finding.acknowledgedAt !== null
  const busy = acknowledge.isPending || restore.isPending
  const scope = acknowledgementScope(finding.runId, latestRunId)

  const handleAcknowledge = () => {
    acknowledge.mutate(
      { id: finding.id, note },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Acknowledged',
            message:
              scope === 'earlier'
                ? `${finding.target} is acknowledged on that earlier pass's record.`
                : `${finding.target} will stay off the outstanding list until it is restored.`,
            color: 'green',
          })
          onClose()
        },
        onError: (err) =>
          notifications.show({
            title: 'Could not acknowledge',
            message: ackErrorText(err, entraConfigured, 'acknowledge'),
            color: 'red',
          }),
      },
    )
  }

  const handleRestore = () => {
    restore.mutate(finding.id, {
      onSuccess: () =>
        notifications.show({
          title: 'Restored',
          message: `${finding.target} is back on the outstanding list.`,
          color: 'blue',
        }),
      onError: (err) =>
        notifications.show({
          title: 'Could not restore',
          message: ackErrorText(err, entraConfigured, 'restore'),
          color: 'red',
        }),
    })
  }

  return (
    <Stack gap="lg" pb="xl">
      <div>
        <SectionTitle>{RULE_LABELS[finding.ruleId] ?? finding.ruleId}</SectionTitle>
        <Text size="sm" c="dimmed" mt={4}>
          {RULE_DESCRIPTIONS[finding.ruleId] ?? 'No description is registered for this rule.'}
        </Text>
      </div>

      <Text size="sm">{finding.message}</Text>

      <MetaList
        items={[
          { label: 'Rule', value: finding.ruleId, mono: true },
          {
            label: 'Provenance',
            // Spelled out rather than shown as a bare enum name: this is what makes two
            // findings on the same column different findings, so it should not read as
            // an incidental tag.
            value: finding.sourceSystem
              ? `${provenanceLabel(finding.sourceSystem)} rows only`
              : 'Not about row data',
          },
          { label: 'Observed', value: finding.observed ?? '—' },
          {
            label: 'Expected',
            // Most of the comb's rules have no expectation to state — the schema
            // carries none — and saying so is more honest than a dash.
            value: finding.expected ?? 'No expectation is declared for this rule',
          },
          {
            label: 'First seen',
            value: `${new Date(finding.firstSeenAt).toLocaleString()} (${relativeTime(finding.firstSeenAt)})`,
          },
          {
            label: 'Last confirmed',
            value: `${new Date(finding.lastSeenAt).toLocaleString()} (${relativeTime(finding.lastSeenAt)})`,
          },
          { label: 'Standing for', value: ageLabel(finding.ageDays, finding.isNew) },
        ]}
      />

      <Divider />

      <div>
        <SectionTitle subtitle="Measured on every pass, retained for 90 days">
          Fill rate over time
        </SectionTitle>
        <FillRateHistory
          schemaName={finding.schemaName}
          tableName={finding.tableName}
          columnName={finding.columnName}
        />
      </div>

      <Divider />

      <div>
        <SectionTitle>Acknowledgement</SectionTitle>

        {isAcknowledged ? (
          <Stack gap="sm" mt="sm">
            <Alert icon={<IconCheck size={16} />} color="green" variant="light">
              <Text size="sm">
                Acknowledged by <strong>{finding.acknowledgedBy ?? 'unknown'}</strong>{' '}
                {relativeTime(finding.acknowledgedAt!)}.
              </Text>
              {finding.acknowledgementNote && (
                <Text size="sm" mt="xs" fs="italic">&ldquo;{finding.acknowledgementNote}&rdquo;</Text>
              )}
            </Alert>
            {gate.kind === 'unavailable' ? (
              <AckUnavailable />
            ) : (
              <>
                {gate.kind === 'refused' && <AckRefused gate={gate} />}
                <AckButton
                  gate={gate}
                  action="restore-finding"
                  variant="default"
                  icon={<IconRotate size={16} />}
                  busy={busy}
                  loading={restore.isPending}
                  onClick={handleRestore}
                >
                  Return to outstanding
                </AckButton>
              </>
            )}
          </Stack>
        ) : (
          <Stack gap="sm" mt="sm">
            {scope === 'earlier' ? (
              // The next pass copies the acknowledgement of the NEWEST record of a
              // condition, so acknowledging this older row marks this record only and
              // need not keep the condition off the next pass's list.
              <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light">
                <Text size="sm">
                  This finding is from an earlier pass. Acknowledging it marks that pass&rsquo;s record
                  only: the next pass carries forward the acknowledgement on the newest record of this
                  condition, so to keep it off the outstanding list, acknowledge it on the latest pass.
                </Text>
              </Alert>
            ) : (
              <Text size="sm" c="dimmed">
                Acknowledging removes this from the outstanding list for everyone, and the next nightly
                pass carries that forward. It does not change the data or stop the measurement.
              </Text>
            )}
            {gate.kind === 'unavailable' ? (
              <AckUnavailable />
            ) : (
              <>
                <Textarea
                  label="Why is this known and expected?"
                  description="Optional, and worth writing — an acknowledgement without one tells the next reader nothing."
                  placeholder="e.g. Not populated by Oracle; filled by the GIS load, which is deferred."
                  value={note}
                  onChange={(e) => setNote(e.currentTarget.value)}
                  autosize
                  minRows={2}
                  maxLength={500}
                  disabled={gate.kind !== 'offer'}
                />
                {gate.kind === 'refused' && <AckRefused gate={gate} />}
                <AckButton
                  gate={gate}
                  action="acknowledge-finding"
                  icon={<IconCheck size={16} />}
                  busy={busy}
                  loading={acknowledge.isPending}
                  onClick={handleAcknowledge}
                >
                  Acknowledge
                </AckButton>
              </>
            )}
          </Stack>
        )}
      </div>
    </Stack>
  )
}

/** Why this session may not acknowledge or restore: the server's reason, and the no-Entra remedy where it applies. */
function AckRefused({ gate }: { gate: Extract<AcknowledgeGate, { kind: 'refused' }> }) {
  return (
    <Alert icon={<IconLock size={16} />} color="gray" variant="light">
      <Text size="sm" data-state="ack-refused">
        You cannot acknowledge or restore findings from this session. {gate.reason}
        {gate.remedy && <> {gate.remedy}</>}
      </Text>
    </Alert>
  )
}

/** The catalogue has not said whether this session may: no control at all, and how an administrator does it. */
function AckUnavailable() {
  return (
    <Text size="sm" c="dimmed" data-state="ack-unavailable">
      {ACK_UNAVAILABLE_TEXT}
    </Text>
  )
}

/**
 * Acknowledge, or "Return to outstanding". Refused, it is shown — dimmed, not hidden — with the server's reason on
 * hover; `data-disabled` rather than `disabled` so the tooltip still receives the pointer, and the click is refused
 * in the handler instead (the Scheduler's ClearHaltButton and CancelRunAction do the same).
 */
function AckButton({
  gate,
  action,
  variant,
  icon,
  busy,
  loading,
  onClick,
  children,
}: {
  gate: Exclude<AcknowledgeGate, { kind: 'unavailable' }>
  action: string
  variant?: string
  icon: ReactNode
  busy: boolean
  loading: boolean
  onClick: () => void
  children: ReactNode
}) {
  const refused = gate.kind === 'refused'
  const reason = refused ? [gate.reason, gate.remedy].filter(Boolean).join(' ') : null

  return (
    <Tooltip label={reason} disabled={!refused} multiline w={300} withArrow>
      <Button
        variant={variant}
        leftSection={refused ? <IconLock size={16} /> : icon}
        data-action={action}
        data-disabled={refused || undefined}
        aria-disabled={refused || undefined}
        disabled={!refused && busy}
        loading={!refused && loading}
        onClick={(event) => {
          if (refused) {
            event.preventDefault()
            return
          }
          onClick()
        }}
      >
        {children}
      </Button>
    </Tooltip>
  )
}
